import { Col, Row } from 'antd';
import ShipToComponent from 'common/partners/components/ship-to-component';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Div, FormItem, FormText, Input } from 'ui';
// eslint-disable-next-line
import ReferenceDataDropdown from 'common/reference-data/containers/reference-data-dropdown-container';
import { updateConsolidation, updateUrgency } from '../containers/parts-actions';

const StyledDiv = styled(Div)`
  border-bottom: 1px solid rgba(217, 224, 230, 0.6) !important;
  padding-bottom: 12px !important;
`;

const PartlistAttributes = ({
  intl: { formatMessage },
  partList: {
    urgency,
    urgencyName,
    partialShipment,
    partialShipmentName,
    specialProcessing,
    specialProcessingName,
    caseLabel1,
    caseLabel2,
    isFrozen,
    consolidationFieldsFrozen,
  },
  form,
  shipTo,
  disabled,
  intl,
  isReadOnly = false,
}) => {
  if (isReadOnly) {
    return (
      <StyledDiv marginBottom={10}>
        <Row type='flex' gutter={16}>
          <Col span={4}>
            <FormItem label={intl.formatMessage({ id: 'order.parts.labels.SHIP_TO' })}>
              <ShipToComponent shipTo={shipTo} readOnly={true} />
            </FormItem>
          </Col>
          <Col span={4}>
            <FormItem label={intl.formatMessage({ id: 'order.parts.labels.URGENCY' })}>
              <FormText>{urgencyName}</FormText>
            </FormItem>
          </Col>
          <Col span={4}>
            <FormItem label={intl.formatMessage({ id: 'order.parts.labels.ORDER_CONSOLIDATION' })}>
              <FormText>{partialShipmentName}</FormText>
            </FormItem>
          </Col>
          <Col span={4}>
            <FormItem label={intl.formatMessage({ id: 'order.parts.labels.SPECIAL_PROCESSING' })}>
              <FormText>{specialProcessingName}</FormText>
            </FormItem>
          </Col>
          <Col span={4}>
            <FormItem label={intl.formatMessage({ id: 'order.parts.labels.CASE_LABELS' })}>
              <FormText>{caseLabel1}</FormText>
            </FormItem>
          </Col>
          <Col span={4}>
            <FormItem label=' ' colon={false}>
              <FormText>{caseLabel2}</FormText>
            </FormItem>
          </Col>
        </Row>
      </StyledDiv>
    );
  }

  const { getFieldDecorator } = form;
  const dispatch = useDispatch();
  // TODO: check which validation needs to happen in draft!
  const urgencyValidator = getFieldDecorator('orderPartList.urgency', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.URGENCY' }) }],
    initialValue: urgency,
  });

  const partialShipmentValidator = getFieldDecorator('orderPartList.partialShipment', {
    rules: [{
      required: true,
      message: intl.formatMessage({ id: 'rules.ORDER_CONSOLIDATION' }),
    }],
    initialValue: partialShipment,
  });

  const specialProcessingValidator = getFieldDecorator('orderPartList.specialProcessing', {
    // eslint-disable-next-line no-undefined
    initialValue: specialProcessing || undefined,
  });

  const caseLabel1Validator = getFieldDecorator('orderPartList.caseLabel1', {
    rules: [{ max: 35, message: intl.formatMessage({ id: 'rules.CASE_LABEL' }) }],
    initialValue: caseLabel1,
  });

  const caseLabel2Validator = getFieldDecorator('orderPartList.caseLabel2', {
    rules: [{ max: 35, message: intl.formatMessage({ id: 'rules.CASE_LABEL' }) }],
    initialValue: caseLabel2,
  });

  return (
    <StyledDiv marginBottom={10}>
      <Row type='flex' gutter={16}>
        <Col span={4}>
          <FormItem label={intl.formatMessage({ id: 'order.parts.labels.SHIP_TO' })}>
            <ShipToComponent shipTo={shipTo} />
          </FormItem>
        </Col>
        <Col span={4}>
          <FormItem label={intl.formatMessage({ id: 'order.parts.labels.URGENCY' })}>
            {!isFrozen && !consolidationFieldsFrozen ?
              <>
                {urgencyValidator(
                  <ReferenceDataDropdown
                    placeholder={formatMessage({
                      id: 'order.parts.SELECT_URGENCY',
                    })}
                    disabled={disabled}
                    type='order'
                    name='urgencies'
                    valueLabel='description'
                    valueKey='code'
                    onChange={v => dispatch(updateUrgency({
                      value: v,
                      parts: form.getFieldValue('orderPartList.parts'),
                    }))}
                  />,
                )}
              </>
              :
              <FormText>{urgencyName}</FormText>
            }
          </FormItem>
        </Col>
        <Col span={4}>
          <FormItem label={intl.formatMessage({ id: 'order.parts.labels.ORDER_CONSOLIDATION' })}>
            {!isFrozen && !consolidationFieldsFrozen ?
              <>
                {partialShipmentValidator(
                  <ReferenceDataDropdown
                    placeholder={formatMessage({
                      id: 'order.parts.SELECT_PARTIAL_SHIPMENT',
                    })}
                    type='order'
                    name='partialShipMents'
                    valueLabel='description'
                    valueKey='code'
                    disabled={disabled}
                    onChange={v => dispatch(updateConsolidation({
                      value: v,
                      parts: form.getFieldValue('orderPartList.parts'),
                    }))}
                  />,
                )}
              </>
              :
              <FormText>{partialShipmentName}</FormText>
            }
          </FormItem>
        </Col>
        <Col span={4}>
          <FormItem label={intl.formatMessage({ id: 'order.parts.labels.SPECIAL_PROCESSING' })}>
            {!isFrozen && !consolidationFieldsFrozen ?
              <>
                {specialProcessingValidator(
                  <ReferenceDataDropdown
                    placeholder={formatMessage({
                      id: 'order.parts.SELECT_SPECIAL_PROCESSING',
                    })}
                    type='order'
                    name='specialProcessing'
                    valueLabel='description'
                    valueKey='code'
                    disabled={disabled}
                    allowClear={true}
                  />,
                )}
              </>
              :
              <FormText>{specialProcessingName}</FormText>
            }
          </FormItem>
        </Col>
        <Col span={4}>
          <FormItem label={intl.formatMessage({ id: 'order.parts.labels.CASE_LABELS' })}>
            {!isFrozen && !consolidationFieldsFrozen ?
              <>
                {caseLabel1Validator(
                  <Input
                    placeholder={formatMessage({
                      id: 'order.parts.CASE_LABEL',
                    })}
                    disabled={disabled}
                  />,
                )}
              </>
              :
              <FormText>{caseLabel1}</FormText>
            }
          </FormItem>
        </Col>
        <Col span={4}>
          <FormItem label=' ' colon={false}>
            {!isFrozen && !consolidationFieldsFrozen ?
              <>
                {caseLabel2Validator(
                  <Input
                    placeholder={formatMessage({
                      id: 'order.parts.CASE_LABEL',
                    })}
                    disabled={disabled}
                  />,
                )}
              </>
              :
              <FormText>{caseLabel2}</FormText>
            }
          </FormItem>
        </Col>
      </Row>
    </StyledDiv>
  );
};

PartlistAttributes.propTypes = {
  partList: PropTypes.shape({
    urgency: PropTypes.string,
    urgencyName: PropTypes.string,
    partialShipment: PropTypes.string,
    partialShipmentName: PropTypes.string,
    specialProcessing: PropTypes.string,
    specialProcessingName: PropTypes.string,
    caseLabel1: PropTypes.string,
    caseLabel2: PropTypes.string,
    isFrozen: PropTypes.bool,
    consolidationFieldsFrozen: PropTypes.bool,
  }),
  intl: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  form: PropTypes.object,
  shipTo: PropTypes.object,
  isReadOnly: PropTypes.bool,
};

export default injectIntl(PartlistAttributes);
